import { writable, derived, get } from 'svelte/store';
import { toast } from 'svelte-sonner';
import { browser } from '$app/env';
import { OperationsApi, isActiveOperation, type TrackedOperation } from '#api/operationsApi.ts';
import { currentUser } from '#api/auth.ts';
import { authStatus } from '#lib/stores/stores.ts';

export const operations = writable<TrackedOperation[]>([]);

/** Drives the AppBar operations dropdown open state so a freshly started op can pop it open. */
export const operationsMenuOpen = writable(false);

const api = new OperationsApi();

/** Request cancellation and toast the outcome; returns whether it was accepted. Callers own their
    per-row "cancelling" state and how they refresh afterwards. */
export async function cancelOperation(trackingId: string): Promise<boolean> {
    try {
        const result = await api.cancel(trackingId);
        if (result.ok) {
            toast.success('Cancellation requested.');
            return true;
        }
    } catch {
        // fall through to the error toast
    }
    toast.error('Failed to cancel operation.');
    return false;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let polling = false;

// Poll fast while something is in flight, slowly when idle (a completed op lingers briefly so the
// tray can show its terminal state before the server ages it out of the index).
const ACTIVE_INTERVAL_MS = 1500;
const IDLE_INTERVAL_MS = 15000;

async function poll() {
    try {
        const result = await api.list();
        if (result.ok && result.data) operations.set(result.data);
    } catch {
        // transient; next tick retries
    }
    schedule();
}

function schedule() {
    if (!browser || !polling) return;
    const hasActive = get(operations).some(isActiveOperation);
    timer = setTimeout(poll, hasActive ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS);
}

// Pipeline operations (imports/exports) are mirrored into the store server-side, so a fresh one
// won't show until the next poll — which is the slow idle cadence when nothing was active yet.
// These events already arrive via SignalR, so refresh promptly when one fires.
const PIPELINE_EVENTS = [
    'ConcordServicing.Common.Events.DataImportChanged',
    'ConcordServicing.Common.Events.DataExportChanged'
];

function onPipelineEvent() {
    refreshOperationsSoon();
}

export function startOperationsPolling() {
    if (!browser || polling) return;
    polling = true;
    PIPELINE_EVENTS.forEach((e) => document.addEventListener(e, onPipelineEvent));
    void poll();
}

export function stopOperationsPolling() {
    polling = false;
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
    if (browser) PIPELINE_EVENTS.forEach((e) => document.removeEventListener(e, onPipelineEvent));
}

/** Only a live staff session polls: obligors have no tray, and a dead session must stop hitting the API. */
export const operationsPollingWanted = derived(
    [authStatus, currentUser],
    ([$status, $user]) => $status === 'authenticated' && $user != null && !$user.isObligor
);

/** Follows auth in this tab — login, logout and session death all flow through here. Returns the cleanup. */
export function bindOperationsPollingToAuth() {
    const unsubscribe = operationsPollingWanted.subscribe((wanted) =>
        wanted ? startOperationsPolling() : stopOperationsPolling()
    );

    return () => {
        unsubscribe();
        stopOperationsPolling();
    };
}

/** Refresh almost immediately — call right after kicking off a new operation. */
export function refreshOperationsSoon() {
    if (!browser || !get(operationsPollingWanted)) return;
    if (!polling) {
        startOperationsPolling();
        return;
    }
    if (timer) clearTimeout(timer);
    timer = setTimeout(poll, 250);
}
